import { useMemo, useState } from "react";
import { Dumbbell, Utensils, Apple, AlertCircle } from "lucide-react";
import { usePlan, useWorkoutHistory } from "@/lib/queries";
import { Spinner } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { PortalHeader } from "@/components/portal/PortalHeader";
import { WorkoutListCard } from "@/components/portal/WorkoutListCard";
import { useStartExecution } from "@/components/portal/useStartExecution";
import {
  countWorkoutItems,
  countDietItems,
  lastExecutionByWorkout,
  shortDateLabel,
  highlightWorkoutId,
} from "@/components/portal/utils";

type Tab = "workouts" | "diets";

/**
 * Tela Treinos: abas Treinos/Dietas, cada card com botão "Iniciar". Portada de
 * `frontend/components/portal/TreinosView.tsx` (Next), trocando props/SSR pelas
 * queries (usePlan/useWorkoutHistory) e router por react-router. O fluxo de
 * iniciar (POST .../execute, 409 → retoma) vive em useStartExecution.
 */
export function TreinosView() {
  const plan = usePlan();
  const history = useWorkoutHistory();
  const { startingId, startError, startWorkout, startDiet } = useStartExecution();
  const [tab, setTab] = useState<Tab>("workouts");

  const workouts = plan.data?.workouts ?? [];
  const diets = plan.data?.diets ?? [];
  const executions = history.data?.executions ?? [];
  const hasDiets = diets.length > 0;

  const lastByWorkout = useMemo(
    () => lastExecutionByWorkout(executions),
    [executions],
  );
  const highlightId = useMemo(
    () => highlightWorkoutId(workouts, executions),
    [workouts, executions],
  );

  if (plan.isLoading) return <Spinner fullscreen />;

  if (plan.isError) {
    return (
      <div className="px-5 pt-8">
        <EmptyState
          icon={AlertCircle}
          title="Não consegui carregar seu plano"
          description="Verifique sua conexão e tente novamente."
        />
      </div>
    );
  }

  const sortedWorkouts = [...workouts].sort((a, b) => a.sort_order - b.sort_order);
  const sortedDiets = [...diets].sort((a, b) => a.sort_order - b.sort_order);

  return (
    <div className="animate-fade-in">
      <PortalHeader title="Treinos" subtitle="Seu plano" />

      <div className="px-5 pb-4">
        {/* Tabs: Treinos / Dieta (só se tiver dieta) */}
        {hasDiets ? (
          <div
            role="tablist"
            aria-label="Tipo de plano"
            className="mb-4 inline-flex w-full items-center gap-1 rounded-full border border-border-subtle bg-surface p-1"
          >
            <button
              type="button"
              role="tab"
              aria-selected={tab === "workouts"}
              onClick={() => setTab("workouts")}
              className={
                "flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all " +
                (tab === "workouts"
                  ? "bg-accent text-ink"
                  : "text-muted hover:text-ink")
              }
            >
              <Dumbbell size={12} aria-hidden />
              Treinos
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "diets"}
              onClick={() => setTab("diets")}
              className={
                "flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-all " +
                (tab === "diets" ? "bg-accent text-ink" : "text-muted hover:text-ink")
              }
            >
              <Utensils size={12} aria-hidden />
              Dieta
            </button>
          </div>
        ) : null}

        {startError ? (
          <div
            role="alert"
            className="mb-3 rounded-2xl border border-danger/40 bg-danger/10 px-3 py-2.5 text-sm text-danger"
          >
            {startError}
          </div>
        ) : null}

        {tab === "workouts" ? (
          sortedWorkouts.length === 0 ? (
            <EmptyState
              icon={Dumbbell}
              title="Sem treino atribuído ainda"
              description="Quando seu treinador montar seu plano, ele aparece aqui."
            />
          ) : (
            <ul className="space-y-2.5">
              {sortedWorkouts.map((w) => (
                <li key={w.id}>
                  <WorkoutListCard
                    title={w.title}
                    exerciseCount={countWorkoutItems(w)}
                    lastExecutedLabel={shortDateLabel(lastByWorkout.get(w.id) ?? null)}
                    highlight={w.id === highlightId}
                    onStart={() => startWorkout(w.id)}
                    starting={startingId === w.id}
                    startDisabled={startingId != null && startingId !== w.id}
                  />
                </li>
              ))}
            </ul>
          )
        ) : sortedDiets.length === 0 ? (
          <EmptyState
            icon={Apple}
            title="Sem dieta atribuída"
            description="Quando seu treinador definir um plano alimentar, ele aparece aqui."
          />
        ) : (
          <ul className="space-y-2.5">
            {sortedDiets.map((d) => (
              <li key={d.id}>
                <WorkoutListCard
                  title={d.title}
                  exerciseCount={countDietItems(d.blocks)}
                  itemNoun="item"
                  lastExecutedLabel={null}
                  highlight={false}
                  onStart={() => startDiet(d.id)}
                  starting={startingId === d.id}
                  startDisabled={startingId != null && startingId !== d.id}
                  startLabel="Iniciar refeição"
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
